import { TNil } from '@app/app.interface';
import { ICustomCtaValue, IGlobalCtaValue } from '@campaign-form/campaign-form.interface';
import type { ICtaTemplateApiDeliverables } from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { IChatbotMulti } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot-multi/chatbot-multi.interface';
import { IChatbot } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot/chatbot.interface';
import { IGamePlay } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/game-play/game-play.interface';
import { IProductPlacement } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/product-placement/product-placement.interface';
import { IImage } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/image-deliverable/image-deliverable.interface';
import { IText } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/text-deliverable/text-deliverable.interface';
import { ITextDescription } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/shared/text-description-deliverable/text-description-deliverable.interface';
import { IVisual } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/visual/visual.interface';

export type TCtaTemplateDeliverableKey = keyof ICtaTemplateApiDeliverables;

export type TNestableDeliverableEntityKey<T> = Extract<keyof T, 'contents' | 'images'>;

/**
 * Entity shape per deliverable collection key — drives {@link TCtaTemplateDeliverableCustomValueStep} nested key checks only.
 */
export interface ICtaTemplateDeliverableEntityMap {
  audioTrackings: IText;
  captions: IText;
  chatbots: IChatbot;
  chatbotMultis: IChatbotMulti;
  contents: IImage;
  customs: IImage;
  descriptions: IText;
  gamePlays: IGamePlay;
  hashTags: IText;
  mentions: IText;
  messages: IText;
  productPlacements: IProductPlacement;
  showcases: IImage;
  talkingPointAis: ITextDescription;
  talkingPoints: IText;
  textTrackings: IText;
  titles: IText;
  visuals: IVisual;
}

type TAssertDeliverableEntityMapMatchesApi =
  Exclude<TCtaTemplateDeliverableKey, keyof ICtaTemplateDeliverableEntityMap> extends never
    ? Exclude<keyof ICtaTemplateDeliverableEntityMap, TCtaTemplateDeliverableKey> extends never
      ? true
      : never
    : never;

const ctaTemplateDeliverableEntityMapMatchesApi: TAssertDeliverableEntityMapMatchesApi = true;

void ctaTemplateDeliverableEntityMapMatchesApi;

export type TCtaTemplateDeliverableCustomValueStep = {
  [K in keyof ICtaTemplateDeliverableEntityMap]: {
    key: K;
    nestedEntityKey?: TNestableDeliverableEntityKey<ICtaTemplateDeliverableEntityMap[K]>;
  };
}[keyof ICtaTemplateDeliverableEntityMap];

export interface ICustomCtaValues {
  customCtaValues?: ICustomCtaValue[] | undefined;
  exclusiveTo?: string | TNil;
}

export interface IEntityWithStoredCustomValues {
  customCtaValues?: ICustomCtaValue[] | null;
  globalCtaValues?: IGlobalCtaValue[] | null;
}

export interface IFormWithCustomValues {
  customCtaValues?: ICustomCtaValue[] | null;
  globalCtaValues?: IGlobalCtaValue[] | null;
  [key: string]: ICustomCtaValue[] | IGlobalCtaValue[] | null | undefined;
}

/** One entry per {@link ICtaTemplateApiDeliverables} key — add a row when that interface grows. */
export const CTA_TEMPLATE_DELIVERABLE_CUSTOM_VALUE_STEPS = [
  { key: 'audioTrackings', nestedEntityKey: 'contents' },
  { key: 'captions', nestedEntityKey: 'contents' },
  { key: 'chatbots' },
  { key: 'chatbotMultis' },
  { key: 'contents', nestedEntityKey: 'images' },
  { key: 'customs', nestedEntityKey: 'images' },
  { key: 'descriptions', nestedEntityKey: 'contents' },
  { key: 'gamePlays' },
  { key: 'hashTags', nestedEntityKey: 'contents' },
  { key: 'mentions', nestedEntityKey: 'contents' },
  { key: 'messages', nestedEntityKey: 'contents' },
  { key: 'productPlacements', nestedEntityKey: 'images' },
  { key: 'showcases', nestedEntityKey: 'images' },
  { key: 'talkingPointAis' },
  { key: 'talkingPoints', nestedEntityKey: 'contents' },
  { key: 'textTrackings', nestedEntityKey: 'contents' },
  { key: 'titles', nestedEntityKey: 'contents' },
  { key: 'visuals' },
] as const satisfies ReadonlyArray<TCtaTemplateDeliverableCustomValueStep>;

/**
 * Build fails if {@link ICtaTemplateApiDeliverables} adds a key not listed in {@link CTA_TEMPLATE_DELIVERABLE_CUSTOM_VALUE_STEPS}.
 * `satisfies` already rejects unknown `key`s / bad `nestedEntityKey`; this rejects omissions (`as const` keeps literal keys).
 */
export type TCtaDeliverableCustomValueStepsIntegrity =
  Exclude<TCtaTemplateDeliverableKey, (typeof CTA_TEMPLATE_DELIVERABLE_CUSTOM_VALUE_STEPS)[number]['key']> extends never
    ? true
    : never;

const ctaDeliverableCustomValueStepsIntegrity: TCtaDeliverableCustomValueStepsIntegrity = true;

void ctaDeliverableCustomValueStepsIntegrity;
